import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileDown, MapPin, Image as ImageIcon, PlusCircle, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import AttendanceMap from "@/components/AttendanceMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AttendanceAdmin() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ employee_id: "", branch_id: "", status: "", activity_type: "", date_from: "", date_to: "" });
  const [photo, setPhoto] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [cForm, setCForm] = useState({ employee_id: "", date: "", status: "present", activity_type: "at_office", description: "", reason: "" });

  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const empName = (id) => employees?.find((e) => e.id === id)?.name || "-";
  const branchName = (id) => branches?.find((b) => b.id === id)?.name || "-";

  const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const { data: rows } = useQuery({ queryKey: ["att-list", qs], queryFn: async () => (await api.get(`/attendance/list?${qs}`)).data });

  const mapPoints = useMemo(() => (rows || [])
    .filter((r) => (r.activity_type === "at_field" || r.activity_type === "meeting_client") && r.latitude != null && r.longitude != null)
    .map((r) => ({ lat: r.latitude, lng: r.longitude, label: r.employee_name, sub: `${r.date} · ${r.activity_type.replace("_", " ")} · ${r.description || ""}` })), [rows]);

  const viewPhoto = async (rec) => {
    if (!rec.photo_path) { toast.info("No photo for this record"); return; }
    try {
      const resp = await api.get(`/files/${rec.photo_path}`, { responseType: "blob" });
      setPhoto(URL.createObjectURL(resp.data));
    } catch { toast.error("Unable to load photo"); }
  };

  const saveCorrect = async () => {
    try { await api.post("/attendance/admin-correct", cForm); toast.success("Attendance saved"); setCorrect(false); qc.invalidateQueries({ queryKey: ["att-list"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Attendance Report</h1>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="view-map-button" onClick={() => setShowMap(true)} variant="outline"><MapIcon className="h-4 w-4 mr-1" /> Map View</Button>
          <Button data-testid="add-correction-button" onClick={() => { setCForm({ employee_id: "", date: "", status: "present", activity_type: "at_office", description: "", reason: "" }); setCorrect(true); }} variant="outline"><PlusCircle className="h-4 w-4 mr-1" /> Add / Correct</Button>
          <a href={`${API}/reports/attendance/export?${qs}`} target="_blank" rel="noreferrer">
            <Button data-testid="attendance-export-btn" className="bg-blue-600 hover:bg-blue-700"><FileDown className="h-4 w-4 mr-1" /> Excel</Button>
          </a>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Select value={filters.employee_id || "all"} onValueChange={(v) => setFilters({ ...filters, employee_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Employees</SelectItem>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.branch_id || "all"} onValueChange={(v) => setFilters({ ...filters, branch_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Branches</SelectItem>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="present">Present</SelectItem><SelectItem value="half_day">Half Day</SelectItem><SelectItem value="absent">Absent</SelectItem></SelectContent>
        </Select>
        <Select value={filters.activity_type || "all"} onValueChange={(v) => setFilters({ ...filters, activity_type: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-activity"><SelectValue placeholder="Activity" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Activity</SelectItem><SelectItem value="at_office">At Office</SelectItem><SelectItem value="at_field">At Field</SelectItem><SelectItem value="meeting_client">Meeting Client</SelectItem></SelectContent>
        </Select>
        <Input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        <Input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
      </div>

      <div className="lg:hidden space-y-3">
        {(rows || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No records.</div>}
        {(rows || []).map((r) => (
          <div key={r.id} data-testid={`attendance-card-${r.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{r.employee_name}</div><div className="text-xs text-slate-400 mt-0.5">{r.date} · {r.server_time || "-"}</div></div>
              <span className={`shrink-0 text-xs px-2 py-1 rounded-full border ${STATUS_META[r.status]?.soft}`}>{STATUS_META[r.status]?.label || r.status}</span>
            </div>
            <div className="mt-2 text-xs text-slate-600">{r.activity_type?.replace("_", " ")}{r.description ? ` · ${r.description}` : ""}</div>
            <div className="mt-1 text-xs text-slate-400">{branchName(r.branch_id)}</div>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <button data-testid={`view-photo-m-${r.id}`} onClick={() => viewPhoto(r)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm"><ImageIcon className="h-4 w-4" /> Photo</button>
              {r.latitude != null && <a href={`https://maps.google.com/?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm"><MapPin className="h-4 w-4" /> GPS</a>}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Employee</th>
            <th className="text-left px-4 py-3">Time</th><th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Activity</th>
            <th className="text-left px-4 py-3 hidden lg:table-cell">Branch</th>
            <th className="text-left px-4 py-3">Photo/GPS</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-slate-400">No records.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id} data-testid={`attendance-record-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3">{r.date}</td>
                <td className="px-4 py-3 font-medium">{r.employee_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.server_time || "-"}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${STATUS_META[r.status]?.soft}`}>{STATUS_META[r.status]?.label || r.status}</span></td>
                <td className="px-4 py-3 hidden md:table-cell text-xs">{r.activity_type?.replace("_", " ")}{r.description && <div className="text-slate-400">{r.description}</div>}</td>
                <td className="px-4 py-3 hidden lg:table-cell">{branchName(r.branch_id)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button data-testid={`view-photo-${r.id}`} onClick={() => viewPhoto(r)} className="p-1.5 text-slate-500 hover:text-blue-600"><ImageIcon className="h-4 w-4" /></button>
                    {r.latitude != null && (
                      <a href={`https://maps.google.com/?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-500 hover:text-emerald-600"><MapPin className="h-4 w-4" /></a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!photo} onOpenChange={(v) => { if (!v) { setPhoto(null); } }}>
        <DialogContent><DialogHeader><DialogTitle>Attendance Photo</DialogTitle></DialogHeader>
          {photo && <img src={photo} alt="attendance" className="w-full rounded-xl" />}
        </DialogContent>
      </Dialog>

      <Dialog open={showMap} onOpenChange={setShowMap}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Field & Client Meeting Check-ins</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2 mb-2">Showing GPS locations for At Field / Meeting With Client records in the current filter.</p>
          <AttendanceMap points={mapPoints} />
        </DialogContent>
      </Dialog>

      <Dialog open={correct} onOpenChange={setCorrect}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add / Correct Attendance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Employee</label>
              <Select value={cForm.employee_id} onValueChange={(v) => setCForm({ ...cForm, employee_id: v })}>
                <SelectTrigger data-testid="correct-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Date</label><Input data-testid="correct-date-input" type="date" value={cForm.date} onChange={(e) => setCForm({ ...cForm, date: e.target.value })} /></div>
            <div>
              <label className="text-xs text-slate-500">Status</label>
              <Select value={cForm.status} onValueChange={(v) => setCForm({ ...cForm, status: v })}>
                <SelectTrigger data-testid="correct-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="present">Present</SelectItem><SelectItem value="half_day">Half Day</SelectItem><SelectItem value="absent">Absent</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-500">Reason (required)</label><Input data-testid="correct-reason-input" value={cForm.reason} onChange={(e) => setCForm({ ...cForm, reason: e.target.value })} /></div>
            <Button data-testid="save-correction-button" onClick={saveCorrect} disabled={!cForm.employee_id || !cForm.date || !cForm.reason} className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
